<script setup lang="ts">
import type { ApprovePriceOverrideErrors } from "@classes-service-types/approve-price-override-errors.ts";
import type { PriceOverrideStatus } from "@classes-service-types/price-override-status.ts";
import { PriceOverrideStatus as PriceOverrideStatusOptions } from "@classes-service-types/price-override-status.ts";
import { datadogLogs } from "@datadog/browser-logs";
import { useQueryClient } from "@tanstack/vue-query";
import Button from "primevue/button";
import ConfirmPopup from "primevue/confirmpopup";
import Dialog from "primevue/dialog";
import Dropdown from "primevue/dropdown";
import Message from "primevue/message";
import Textarea from "primevue/textarea";
import Toast from "primevue/toast";
import { useConfirm } from "primevue/useconfirm";
import { useToast } from "primevue/usetoast";
import { computed, ref, toRefs } from "vue";
import { modalPresets } from "@/components/OperationsApp/components/Modals/styles.ts";
import { REJECTED_DISPLAY_TEXT } from "@/components/OperationsApp/constants.ts";
import { QUERY_KEY as LIST_QUERY_KEY } from "@/providers/priceOverrides/useGetPriceOverrides.ts";
import { QUERY_KEY as COUNTS_QUERY_KEY } from "@/providers/priceOverrides/useGetPriceOverridesCount.ts";
import { usePriceOverridesApprove } from "@/providers/priceOverrides/usePriceOverridesApprove.ts";
import { usePriceOverridesReject } from "@/providers/priceOverrides/usePriceOverridesReject.ts";
import { BULK_UPDATE_STATUS_OPTIONS } from "./constants.ts";
import { priceOverrideBulkUpdateModalPresets } from "./styles.ts";
import type { Emits, Props } from "./types.ts";

defineOptions({
  name: "PriceOverrideBulkUpdateModal",
});

const props = withDefaults(defineProps<Props>(), {
  modalVisible: false,
});
const emit = defineEmits<Emits>();

const confirm = useConfirm();
const toast = useToast();
const queryClient = useQueryClient();

const { data, modalVisible } = toRefs(props);

const notes = ref("");
const updatedStatus = ref<PriceOverrideStatus>(PriceOverrideStatusOptions.Approved);

const apiErrors = ref<Array<ApprovePriceOverrideErrors>>([]);

const resetResults = async () => {
  await queryClient.refetchQueries({ queryKey: [LIST_QUERY_KEY], type: "all", exact: false });
  await queryClient.refetchQueries({ queryKey: [COUNTS_QUERY_KEY], type: "all", exact: false });
};

const responseErrors = computed(() => {
  const dataArray = data.value ?? [];
  const retVal =
    apiErrors.value?.map((apiError) => {
      const priceOverride = dataArray.find((rec) => rec.id === apiError.override_id);
      return `Failed to ${updatedStatus.value === PriceOverrideStatusOptions.Approved ? "Approve" : "Reject"} price override ${priceOverride?.id} for ${priceOverride?.participant_fullname} - ${priceOverride?.participant_email}`;
    }) ?? [];
  return retVal;
});

const { mutate: approve, isPending: isApproving } = usePriceOverridesApprove({
  onSuccess: async (data, variables, context) => {
    if (Array.isArray(data?.errors) && data.errors.length > 0) {
      // TODO: 12/11/2025 Remove datadog logging after code stabilizes
      datadogLogs.logger.warn("Failed to approve price overrides for ids.", {
        errors: data.errors,
      });
      apiErrors.value = data.errors;
    } else {
      toast.add({
        severity: "success",
        summary: "Price Override",
        detail: "Approve has succeeded.",
        group: "br-bum",
        life: 5000,
      });
      await resetResults();
      hideModal();
    }
  },
  onError: (error, variables) => {
    // TODO: 12/11/2025 Remove datadog logging after code stabilizes
    datadogLogs.logger.warn("Failed calling approve price override.", {
      error: error.message,
      priceOverrideIds: variables?.payload?.ids ?? "unknown",
      decisionNotes: variables?.payload?.decision_notes ?? "unknown",
    });
    toast.add({
      severity: "error",
      summary: "Price Override",
      detail: "Approve has failed.",
      group: "br-bum",
      life: 5000,
    });
  },
  retry: 0,
});

const { mutate: decline, isPending: isDeclining } = usePriceOverridesReject({
  onSuccess: async (data, variables) => {
    if (Array.isArray(data?.errors) && data.errors.length > 0) {
      // TODO: 12/11/2025 Remove datadog logging after code stabilizes
      datadogLogs.logger.warn("Failed to reject price overrides for ids.", {
        errors: data.errors,
      });
      apiErrors.value = data.errors;
    } else {
      toast.add({
        severity: "success",
        summary: "Price Override",
        detail: "Decline has succeeded.",
        group: "br-bum",
        life: 5000,
      });
      await resetResults();
      hideModal();
    }
  },
  onError: (error, variables) => {
    // TODO: 12/11/2025 Remove datadog logging after code stabilizes
    datadogLogs.logger.warn("Failed calling reject price override.", {
      error: error.message,
      priceOverrideIds: variables?.payload?.ids ?? "unknown",
      decisionNotes: variables?.payload?.decision_notes ?? "unknown",
    });
    toast.add({
      severity: "error",
      summary: "Price Override",
      detail: "Decline has failed.",
      group: "br-bum",
      life: 5000,
    });
  },
  retry: 0,
});

const statusText = computed(() =>
  updatedStatus.value === PriceOverrideStatusOptions.Approved
    ? PriceOverrideStatusOptions.Approved
    : REJECTED_DISPLAY_TEXT,
);

const confirmBulkUpdate = (event) => {
  confirm.require({
    target: event.currentTarget,
    message: `Are you sure you want to ${statusText.value} ${data?.value?.length} price overrides?`,
    icon: "pi pi-exclamation-triangle",
    rejectClass: "p-button-secondary p-button-outlined p-button-sm",
    acceptClass: "p-button-sm",
    rejectLabel: "Cancel",
    acceptLabel: "Save",
    accept: () => {
      if (Array.isArray(data?.value) && data?.value?.length > 0) {
        const payload = { ids: data.value.map((item) => item.id), decision_notes: notes.value };
        if (updatedStatus.value === PriceOverrideStatusOptions.Approved) {
          approve({ payload });
        } else {
          decline({ payload });
        }
      }
    },
  });
};

const updateModalVisibility = (value) => {
  apiErrors.value = [];
  if (!value) {
    emit("hide");
  }
};

const hideModal = () => {
  apiErrors.value = [];
  emit("hide");
};
</script>

<template>
  <Toast position="bottom-right" group="br-bum" />
  <Dialog :visible="modalVisible" @update:visible="updateModalVisibility" modal :dismissable-mask="true" :close-on-escape="true" :pt="modalPresets.dialog">
    <template #header>
      <div class="font-montserrat inline-flex align-items-center justify-content-center">
        <div>Bulk Edit - Price Override</div>
      </div>
    </template>
    <template #default>
      <div class="grid grid-cols-2 gap-x-4 gap-y-6">
        <div class="col-span-2">
          <header class="font-semibold mb-2">Status Change For</header>
          <p>{{ data?.length || "" }} Students</p>
        </div>
        <div class="col-span-2">
          <header class="font-semibold mb-2">Updated Status</header>
          <Dropdown option-label="label" option-value="value" :options="BULK_UPDATE_STATUS_OPTIONS" v-model="updatedStatus" />
        </div>
        <div class="col-span-2">
          <header class="font-semibold mb-2">Notes</header>
          <Textarea class="w-full" v-model="notes" autoResize rows="5" maxlength="500" />
        </div>
      </div>
      <div v-if="responseErrors?.length > 0" class="flex flex-col max-h-[120px] overflow-auto my-4">
        <Message v-for="responseError in responseErrors" severity="error" :pt="priceOverrideBulkUpdateModalPresets.message">{{ responseError }}</Message>
      </div>
    </template>
    <template #footer>
      <div class="flex ml-auto">
        <Button @click="hideModal" class="mr-2" outlined>Cancel</Button>
        <Button @click="confirmBulkUpdate($event)" :loading="isApproving || isDeclining">Submit</Button>
        <ConfirmPopup></ConfirmPopup>
      </div>
    </template>
  </Dialog>
</template>

<style scoped>
</style>
